<template>
    <div class="AuthForm">
        <!-- Social buttons - icon only -->
        <template v-if="!forgottenPassword && state !== 'passwordResetSent' && registrationState !== 'verificationSent'">
            <div class="flex justify-center gap-3 mb-4">
                <LogInWithGoogle
                    :create-account="authMethod === 'createUserWithEmailAndPassword'"
                    @error="socialLoginErrorHandler"
                    @success="moveNext"
                />
                <LogInWithFacebook
                    :create-account="authMethod === 'createUserWithEmailAndPassword'"
                    @error="socialLoginErrorHandler"
                    @success="moveNext"
                />
            </div>

            <div class="border-t border-gray-200 dark:border-default mb-4"></div>
        </template>

        <!-- Password reset message -->
        <template v-if="state === 'passwordResetSent'">
            <div class="text-center py-4">
                <div class="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-950/40 flex items-center justify-center">
                    <svg class="w-6 h-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p class="text-gray-700 dark:text-txt-body">Email s odkazom pre nastavenie nového hesla bol odoslaný.</p>
            </div>
        </template>

        <!-- Verification message -->
        <template v-if="registrationState === 'verificationSent'">
            <div class="text-center py-4">
                <!-- Email icon -->
                <div class="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 dark:bg-primary-950/40 flex items-center justify-center">
                    <svg class="w-7 h-7 text-primary-600 dark:text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                </div>

                <!-- Message -->
                <p class="text-gray-900 dark:text-txt-headings mb-1">Overovací email sme poslali na</p>
                <p class="text-gray-900 dark:text-txt-headings font-semibold mb-5">{{ registeredEmail }}</p>

                <!-- Status message -->
                <div
v-if="verificationMessage" :class="[
                    'mb-4 p-3 rounded-lg text-sm',
                    verificationMessage.type === 'error' ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300' : 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300'
                ]">
                    {{ verificationMessage.text }}
                </div>

                <!-- Check verification button -->
                <button
                    type="button"
                    @click="checkEmailVerification"
                    :disabled="isCheckingVerification"
                    class="w-full rounded-lg bg-gray-900 px-4 py-3 text-sm font-semibold text-white hover:bg-gray-900 disabled:opacity-50 mb-3"
                >
                    <span v-if="isCheckingVerification" class="flex items-center justify-center gap-2">
                        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Overujem...
                    </span>
                    <span v-else>Už som overil/a email</span>
                </button>

                <!-- Resend link -->
                <button
                    type="button"
                    @click="resendVerificationEmail"
                    :disabled="isResending"
                    class="text-sm text-gray-500 dark:text-txt-body hover:text-primary-600 hover:dark:text-primary-300 disabled:opacity-50"
                >
                    {{ isResending ? 'Odosielam...' : 'Poslať email znova' }}
                </button>
            </div>
        </template>

        <!-- Main form -->
        <template v-if="state !== 'passwordResetSent' && registrationState !== 'verificationSent'">
            <form class="flex flex-col space-y-2 md:space-y-3" @submit.prevent="handleFormSubmit">
                <div
                    v-if="state === 'error'"
                    class="p-2 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-300 text-xs"
                >
                    {{ errors.general }}
                </div>

                <div class="w-full">
                    <CFormGroup :error="errors.email">
                        <!-- Show email as editable text when in password step (registration) -->
                        <div v-if="isRegistration && showPasswordStep" class="flex items-center gap-2">
                            <span class="flex-1 text-sm text-gray-700 dark:text-txt-body truncate">{{ credentials.email }}</span>
                            <button
                                type="button"
                                class="text-xs text-gray-500 dark:text-txt-body hover:text-gray-700 hover:dark:text-txt-body"
                                @click="showPasswordStep = false"
                            >
                                Zmeniť
                            </button>
                        </div>
                        <CFormInput
                            v-else
                            v-model="credentials.email"
                            autocomplete="username"
                            type="email"
                            class="w-full text-xs"
                            data-test-id="authForm.email"
                            :disabled="isLoading"
                            name="email"
                            placeholder="Email"
                        />
                    </CFormGroup>
                </div>

                <div v-if="shouldShowPassword" class="w-full">
                    <div class="flex items-center justify-between mb-1" v-if="!hideResetPassword">
                        <span></span>
                        <button
                            type="button"
                            class="text-xs text-gray-500 dark:text-txt-body hover:text-gray-700 hover:dark:text-txt-body transition-colors"
                            @click.prevent="setForgottenPassword()"
                        >
                            Zabudnuté?
                        </button>
                    </div>
                    <CFormGroup :error="errors.password">
                        <CFormInput
                            v-model="credentials.password"
                            autocomplete="password"
                            class="w-full text-xs"
                            data-test-id="authForm.password"
                            :disabled="isLoading"
                            name="password"
                            type="password"
                            :placeholder="isRegistration ? 'Vytvor si heslo' : 'Heslo'"
                        />
                    </CFormGroup>
                </div>

                <button
                    data-test-id="authForm.submit"
                    :disabled="isLoading"
                    type="submit"
                    class="mt-1 md:mt-2 w-full rounded-lg bg-gray-900 dark:bg-white px-4 py-2.5 md:py-3 text-sm font-semibold text-white dark:text-gray-900 transition-colors hover:bg-gray-900 dark:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span v-if="isLoading" class="flex items-center justify-center gap-2">
                        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ buttonLabelByState }}
                    </span>
                    <span v-else>{{ buttonLabelByState }}</span>
                </button>
            </form>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useApplicationStore } from '@/stores/application'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

import { useRouter, useRoute } from 'vue-router'
import {
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signInWithEmailAndPassword
} from 'firebase/auth'
import { ValidationErrors } from 'last-validator'

import type { UserId } from '#shared/types/ids'
import type { Credentials, UserProfile } from '#shared/types/User'
import type { LoadingState } from '#shared/types/LoadingState'
import CButton from '@/components/ui/CButton.vue'
import CFormGroup from '@/components/ui/CFormGroup.vue'
import CFormInput from '@/components/ui/CFormInput.vue'
import CLineBreak from '@/components/ui/CLineBreak.vue'
import { useEmailVerification } from '@/composables/auth/useEmailVerification'
import { validateEmail } from '@/validators/privateProfile'
import { validateCredentials } from '@/validators/userProfile'

enum AuthMethod {
    signInWithEmailAndPassword = 'signInWithEmailAndPassword',
    createUserWithEmailAndPassword = 'createUserWithEmailAndPassword'
}

interface Props {
    buttonLabel: string
    passwordLabel?: string
    hideResetPassword?: boolean
    authMethod: string
    redirect?: string
    showResetPassword?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    passwordLabel: 'Heslo',
    hideResetPassword: false,
    redirect: '',
    showResetPassword: false
})

const emit = defineEmits(['update:resetPassword', 'update:loading'])

const router = useRouter()
const route = useRoute()
const { $firebase, $auth } = useNuxtApp()
const applicationStore = useApplicationStore()
const { sendVerificationEmail } = useEmailVerification()

const credentials = ref<Credentials>({} as Credentials)
// E-mail, ktorý stránka vopred pozná (napr. platiteľ členského z osobného odkazu).
// Človek ho môže prepísať — je to len predvyplnenie.
const authModalPrefillEmail = useState<string>('authModalPrefillEmail', () => '')
if (authModalPrefillEmail.value) credentials.value.email = authModalPrefillEmail.value
const errors = ref<ValidationErrors>({} as ValidationErrors)
const state = ref<LoadingState | 'passwordResetSent'>('idle')
const forgottenPassword = ref(false)
const showPasswordStep = ref(false) // For progressive registration flow
const registrationState = ref<'idle' | 'registering' | 'verificationSent' | 'error'>('idle')
const registeredEmail = ref('')
const isCheckingVerification = ref(false)
const isResending = ref(false)
const verificationMessage = ref<{ type: 'error' | 'success', text: string } | null>(null)
let cancelSignInListener: any = null

// Watch for reset password prop changes
watch(
    () => props.showResetPassword,
    (newVal) => {
        forgottenPassword.value = newVal
        errors.value = {} as ValidationErrors
    }
)

const userProfile = computed<UserProfile | undefined>(() => applicationStore.userProfile)

const isRegistration = computed(() => props.authMethod === AuthMethod.createUserWithEmailAndPassword)

// For registration: show password only after email step
const shouldShowPassword = computed(() => {
    if (forgottenPassword.value) return false
    if (!isRegistration.value) return true // Login always shows password
    return showPasswordStep.value // Registration shows password only after email validated
})

const buttonLabelByState = computed(() => {
    if (forgottenPassword.value) return 'Resetovať'
    if (isRegistration.value && !showPasswordStep.value) return 'Pokračovať'
    return props.buttonLabel
})

const isLoading = computed(() => state.value === 'loading')

function getAuthErrorMessage(error: any) {
    let errorMsg
    switch (error.code) {
        case 'auth/wrong-password':
        case 'auth/invalid-email':
        case 'auth/user-not-found':
        case 'auth/invalid-credential':
            errorMsg = 'Nesprávna emailová adresa alebo heslo.'
            break
        case 'auth/weak-password':
            errorMsg = 'Heslo by malo mať aspoň 6 znakov.'
            break
        case 'auth/email-already-in-use':
            errorMsg = 'E-mailovú adresu už používa iný účet.'
            break
        case 'auth/account-exists-with-different-credential':
            errorMsg = 'Užívateľ bol zaregistrovaný prostredníctvom iného poskytovateľa'
            break
        default:
            errorMsg = 'Vyskytla sa nešpecifikovaná chyba. Kontaktujte help@growni.sk'
    }
    return errorMsg
}

async function performAuthMethod() {
    switch (props.authMethod) {
        case AuthMethod.signInWithEmailAndPassword:
            const loginCredential = await signInWithEmailAndPassword($auth, credentials.value.email, credentials.value.password)

            // Check if user needs to verify their email
            if (loginCredential.user && !loginCredential.user.emailVerified) {
                // User logged in but hasn't verified email - show verification screen
                registeredEmail.value = credentials.value.email
                registrationState.value = 'verificationSent'
            }
            break
        case AuthMethod.createUserWithEmailAndPassword:
            const userCredential = await createUserWithEmailAndPassword(
                $auth,
                credentials.value.email,
                credentials.value.password
            )

            // Send verification email immediately with proper redirect URL
            await sendVerificationEmail(userCredential.user, verificationReturnTo())

            // Store email for display in success message
            registeredEmail.value = credentials.value.email

            // Set state to show verification message
            // Note: Auth state is handled automatically by Firebase auth plugin
            registrationState.value = 'verificationSent'
            break
        default:
            throw new Error(`Unsupported auth method "${props.authMethod}".`)
    }
}

function setForgottenPassword() {
    emit('update:resetPassword', true)
    forgottenPassword.value = true
    errors.value = {} as ValidationErrors
}

async function handleFormSubmit() {
    if (forgottenPassword.value) {
        return resetPassword()
    }

    // For registration: handle two-step flow
    if (isRegistration.value && !showPasswordStep.value) {
        // Step 1: Validate email and proceed to password step
        if (await validateEmailField()) {
            showPasswordStep.value = true
        }
        return
    }

    // Step 2 (registration) or normal login
    return submit()
}

function setLoading(isLoading: boolean) {
    emit('update:loading', isLoading)
}

async function validateCredentialsForm() {
    const { errors: validationErrors, isValid } = await validateCredentials(credentials.value)
    errors.value = validationErrors
    return isValid
}

async function submit() {
    if (!(await validateCredentialsForm())) {
        return
    }

    try {
        state.value = 'loading'
        registrationState.value = 'registering'
        setLoading(true)
        await performAuthMethod()

        // Reset loading state after successful auth
        state.value = 'idle'

        // For login: proceed as normal (unless email needs verification)
        if (props.authMethod === AuthMethod.signInWithEmailAndPassword && registrationState.value !== 'verificationSent') {
            moveNext()
        }
        // For registration and unverified login: stay on verification state (don't call moveNext)
        // registrationState is set to 'verificationSent' in performAuthMethod when needed

    } catch (e: any) {
        errors.value = {
            general: getAuthErrorMessage(e)
        }
        state.value = 'error'
        registrationState.value = 'error'
        console.error(e)
    } finally {
        setLoading(false)
    }
}

// Only one post-sign-in redirect may run. `moveNext()` (social buttons + email login) and the
// profile-hydration watcher below both consumed `beforeLogInLocation`, so whichever fired first
// won: with Google the watcher often won and hard-redirected a brand-new user straight to the
// protected page they came from, skipping /auth/registration (no name, no rulesAccepted).
let redirectHandled = false

async function moveNext() {
    // Claim the redirect before the first await — the watcher may fire while we read Firestore.
    redirectHandled = true

    // Decide whether the signed-in user has *finished* registration: a name AND accepted
    // platform rules. Keying off authMethod alone missed social sign-ups — a brand-new user
    // signing in with Google (on any surface) skips registration and never gets rulesAccepted,
    // leaving them stuck behind every gate that checks it. So gate on real state instead.
    // Social popups resolve before the auth listener hydrates the store, so re-read from
    // Firestore when the store still looks incomplete.
    const uid = $auth.currentUser?.uid
    let registrationComplete = Boolean(
        applicationStore.userProfile?.firstName && applicationStore.userPrivateProfile?.rulesAccepted
    )
    if (!registrationComplete && uid) {
        const [profile, privateProfile] = await Promise.all([
            $firebase.loadDocument('profiles', uid),
            $firebase.loadDocument('users', uid)
        ])
        registrationComplete = Boolean((profile as any)?.firstName && (privateProfile as any)?.rulesAccepted)
    }

    // Unified redirect logic for both login and registration
    const redirectAfterLoginUrl = props.redirect || route.query.redirect || route.redirectedFrom?.name

    // Check localStorage for saved location
    const beforeLogInLocation = localStorage.getItem('beforeLogInLocation')
    if (beforeLogInLocation) {
        localStorage.removeItem('beforeLogInLocation')
        if (!registrationComplete) {
            // Incomplete user: collect missing profile data (name, rules, livingAbroad) first
            router.push({ name: 'auth-registration', query: { redirect: beforeLogInLocation } })
        } else {
            window.location.href = beforeLogInLocation
        }
        return
    }

    if (!registrationComplete) {
        // Incomplete user without a saved location: finish registration, then continue on
        const dest = (typeof redirectAfterLoginUrl === 'string' && redirectAfterLoginUrl) || '/profile/welcome'
        router.push({ name: 'auth-registration', query: { redirect: dest } })
        return
    }

    if (redirectAfterLoginUrl && typeof redirectAfterLoginUrl === 'string') {
        router.push(
            redirectAfterLoginUrl.includes('/') ? { path: redirectAfterLoginUrl } : { name: redirectAfterLoginUrl }
        )
    }
    // For login of a completed user, modal will auto-close and user stays on current page
}

async function validateEmailField() {
    const { errors: validationErrors, isValid } = await validateEmail(credentials.value.email)
    errors.value = validationErrors
    return isValid
}

async function resetPassword() {
    if (!(await validateEmailField())) {
        return
    }

    try {
        state.value = 'loading'
        setLoading(true)
        await sendPasswordResetEmail($auth, credentials.value.email)
        state.value = 'passwordResetSent'
    } catch (e: any) {
        let errorMsg = 'Vyskytla sa nešpecifikovaná chyba.'

        if (e.code === 'auth/user-not-found') {
            errorMsg = `Neexistuje žiadny používateľský záznam zodpovedajúci tomuto identifikátoru. Používateľ mohol byť odstránený. V prípade nezrovnalostí, kontaktuj prosím help@growni.sk`
        }

        errors.value = { general: errorMsg }
        state.value = 'error'
    } finally {
        setLoading(false)
    }
}

async function checkEmailVerification() {
    if (!$auth.currentUser) return

    try {
        isCheckingVerification.value = true
        verificationMessage.value = null

        // Reload user to get fresh emailVerified status
        await $auth.currentUser.reload()

        if ($auth.currentUser.emailVerified) {
            // Email verified - proceed to app
            verificationMessage.value = { type: 'success', text: 'Email overený!' }

            // Short delay to show success message
            setTimeout(() => {
                registrationState.value = 'idle'
                moveNext()
            }, 500)
        } else {
            verificationMessage.value = { type: 'error', text: 'Email ešte nebol overený. Skontroluj svoju schránku.' }
        }
    } catch (e: any) {
        verificationMessage.value = { type: 'error', text: 'Nepodarilo sa overiť. Skús to znova.' }
    } finally {
        isCheckingVerification.value = false
    }
}

// A protected page (e.g. the mentoring invite link) bounces signed-out visitors to "/" and opens
// this modal there, so the current URL is "/". The link in the verification mail must lead back
// to the page they came for, as moveNext() does after "Už som overil/a email".
function verificationReturnTo(): string | undefined {
    return localStorage.getItem('beforeLogInLocation') || undefined
}

async function resendVerificationEmail() {
    if (!$auth.currentUser) return

    try {
        isResending.value = true
        verificationMessage.value = null

        await sendVerificationEmail($auth.currentUser, verificationReturnTo())

        verificationMessage.value = { type: 'success', text: 'Email bol odoslaný znova.' }
    } catch (e: any) {
        if (e.code === 'auth/too-many-requests') {
            verificationMessage.value = { type: 'error', text: 'Príliš veľa pokusov. Skús to neskôr.' }
        } else {
            verificationMessage.value = { type: 'error', text: 'Nepodarilo sa odoslať email.' }
        }
    } finally {
        isResending.value = false
    }
}

function completeRegistration() {
    // Reset verification state
    registrationState.value = 'idle'

    // Close the modal
    const { closeAuthModal } = useAuthModal()
    closeAuthModal()

    // Only redirect if there was a saved location before login
    // Otherwise, let user continue on current page (landing page, etc.)
    const beforeLogInLocation = localStorage.getItem('beforeLogInLocation')

    if (beforeLogInLocation) {
        localStorage.removeItem('beforeLogInLocation')
        window.location.href = beforeLogInLocation
    }
    // Don't auto-redirect to welcome page - user can continue browsing
    // They can verify email later when they try to access protected features
}

async function goBackFromVerification() {
    // Reset to idle state
    registrationState.value = 'idle'

    // If user is already logged in (unverified login scenario), log them out
    // This allows them to come back and verify later
    if (applicationStore.userId && applicationStore.isEmailVerified === false) {
        await applicationStore.logout()
        const { closeAuthModal } = useAuthModal()
        closeAuthModal()
    }
    // Otherwise (registration scenario), just show the form again
}

function handleSignIn() {
    // Safety net for sign-ins where the store hydrates after the submit handler finished.
    // It must not race moveNext() nor skip registration, so it just delegates to it.
    if (redirectHandled) return

    // Unverified email registrations stay on the verification screen — don't navigate away.
    if (applicationStore.isEmailVerified === false) return

    moveNext()
}

function socialLoginErrorHandler(error: any) {
    state.value = 'error'
    errors.value = {
        general: getAuthErrorMessage(error)
    }
    console.error('socialLoginErrorHandler login not provided ', error === 'emailnotexist' ? '' : error)
}

onMounted(() => {
    // Watch for user profile changes
    cancelSignInListener = watch(
        () => userProfile.value?.id,
        (userId: UserId | undefined) => {
            if (userId) {
                handleSignIn()
            }
        }
    )
})

onUnmounted(() => {
    if (cancelSignInListener) {
        cancelSignInListener()
    }
})
</script>
